import type { AnalyticsFeeder, GrafanaReportFilters, ReportingReportView, ReportingRun } from "@/lib/api";
import { dashboardRangeError, dashboardTime } from "@/lib/dashboard-time";
import { CATALOGUE_FILTERS, REPORTS, reportSearchParams, visibleReportFeeders, type ActiveGrafanaReport } from "@/lib/report-filters";

/** IDs in app links identify context, never authorize access or switch accounts. */
export function reportOrganizationMatches(search: URLSearchParams, active: number | null): boolean {
  if (!search.has("organization_id")) return true;
  const values = search.getAll("organization_id");
  return (
    values.length === 1 &&
    /^[1-9]\d*$/.test(values[0] ?? "") &&
    Number.isSafeInteger(Number(values[0])) &&
    Number(values[0]) === active
  );
}

/** Accept only report state: never copy arbitrary query parameters or embed URLs. */
export function createReportLink(
  report: ActiveGrafanaReport,
  filters: GrafanaReportFilters,
  organizationId: number,
  origin: string,
  now = Date.now(),
): string {
  if (!Number.isSafeInteger(organizationId) || organizationId <= 0)
    throw new Error("Choose an organization before sharing.");
  const error = dashboardRangeError(filters.from, filters.to, now);
  if (error) throw new Error(error);
  const fixed = {
    ...filters,
    from: String(dashboardTime(filters.from, false, now)),
    to: String(dashboardTime(filters.to, true, now)),
  };
  const definition = REPORTS.find((entry) => entry.report === report);
  if (!definition) throw new Error("Unknown report.");
  const url = new URL(definition.path, new URL(origin).origin);
  url.search = reportSearchParams(fixed).toString();
  url.searchParams.set("organization_id", String(organizationId));
  return url.toString();
}

export const REPORT_DRAFT_PARAM = "report_draft";
const MAX_DRAFT_LENGTH = 12000;
export type ReportDraft = {
  organization_id: number;
  report_view: ReportingReportView;
  feeder_ids: string[];
  from_time: string;
  to_time: string;
};

function record(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function exactKeys(value: Record<string, unknown>, keys: string[]): boolean {
  return Object.keys(value).length === keys.length && keys.every((key) => Object.hasOwn(value, key));
}

export function validReportView(value: unknown): value is ReportingReportView {
  if (!record(value) || !exactKeys(value, ["report", "filters"]) ||
    !REPORTS.some(({ report }) => report === value.report) || !record(value.filters)) return false;
  const filters = value.filters;
  return exactKeys(filters, [...CATALOGUE_FILTERS.map(({ key }) => key), "groupby"]) &&
    CATALOGUE_FILTERS.every(({ key }) =>
      typeof filters[key] === "string" && (filters[key] as string).length > 0 &&
      (filters[key] as string).length <= 256 &&
      ![...(filters[key] as string)].some((character) => character.charCodeAt(0) < 32)) &&
    ["Overall", "DISCO", "Substation"].includes(String(filters.groupby)) &&
    filters.program !== "__ALL__" &&
    (value.report !== "performance" || filters.feeder !== "__ALL__");
}

export function reportScopeMatches(view: ReportingReportView, ids: string[], feeders: readonly AnalyticsFeeder[]): boolean {
  if (!validReportView(view)) return false;
  const matched = visibleReportFeeders(feeders, { ...view.filters, from: "now-1d", to: "now" });
  return ids.length > 0 && new Set(ids).size === ids.length && matched.length === ids.length &&
    matched.every((feeder) => ids.includes(feeder.dev_eui));
}

export function createReportDraftLink(
  workspace: "exports" | "schedules",
  report: ActiveGrafanaReport,
  filters: GrafanaReportFilters,
  organizationId: number,
  feeders: readonly AnalyticsFeeder[],
  now = Date.now(),
): string {
  // Reuse the canonical app-link range validation and freeze both boundaries once.
  const fixed = new URL(createReportLink(report, filters, organizationId, "https://draft.invalid", now));
  const context = Object.fromEntries([
    ...CATALOGUE_FILTERS.map(({ key }) => [key, filters[key]]),
    ["groupby", filters.groupby],
  ]) as ReportingReportView["filters"];
  const draft: ReportDraft = {
    organization_id: organizationId,
    report_view: { report, filters: context },
    feeder_ids: visibleReportFeeders(feeders, filters).map(({ dev_eui }) => dev_eui),
    from_time: new Date(Number(fixed.searchParams.get("from"))).toISOString(),
    to_time: new Date(Number(fixed.searchParams.get("to"))).toISOString(),
  };
  if (!reportScopeMatches(draft.report_view, draft.feeder_ids, feeders)) throw new Error("Report scope is unavailable.");
  const serialized = JSON.stringify(draft);
  if (serialized.length > MAX_DRAFT_LENGTH) throw new Error("Report scope is too large. Narrow the filters.");
  return `/reporting/${workspace}?${new URLSearchParams({ [REPORT_DRAFT_PARAM]: serialized })}`;
}

export function readReportDraft(
  search: URLSearchParams, organizationId: number | null, feeders: readonly AnalyticsFeeder[],
): ReportDraft | null {
  const serialized = search.get(REPORT_DRAFT_PARAM);
  if (!serialized || serialized.length > MAX_DRAFT_LENGTH || search.getAll(REPORT_DRAFT_PARAM).length !== 1) return null;
  try {
    const draft: unknown = JSON.parse(serialized);
    if (!record(draft) || !exactKeys(draft, ["organization_id", "report_view", "feeder_ids", "from_time", "to_time"]) ||
      !Number.isSafeInteger(draft.organization_id) || Number(draft.organization_id) <= 0 ||
      draft.organization_id !== organizationId || !validReportView(draft.report_view) ||
      !Array.isArray(draft.feeder_ids) || !draft.feeder_ids.every((id) => typeof id === "string") ||
      typeof draft.from_time !== "string" || typeof draft.to_time !== "string" ||
      !/^\d{4}-\d\d-\d\dT/.test(draft.from_time) || !/^\d{4}-\d\d-\d\dT/.test(draft.to_time) ||
      dashboardRangeError(draft.from_time, draft.to_time) ||
      !reportScopeMatches(draft.report_view, draft.feeder_ids, feeders)) return null;
    return draft as ReportDraft;
  } catch { return null; }
}

/** API strings never become external navigation or carry unrelated query state. */
export function safeRunReportUrl(run: ReportingRun, organizationId: number | null, origin: string,
  feeders: readonly AnalyticsFeeder[] = []): string | null {
  if (!run.report_url || !organizationId || !validReportView(run.report_view)) return null;
  if (!reportScopeMatches(run.report_view, run.feeder_ids ?? [], feeders)) return null;
  try {
    const expected = new URL(createReportLink(run.report_view.report,
      { ...run.report_view.filters, from: run.from_time, to: run.to_time }, organizationId, origin));
    const actual = new URL(run.report_url, origin);
    if (actual.origin !== expected.origin || actual.pathname !== expected.pathname ||
      actual.hash || actual.username || actual.password) return null;
    if ([...actual.searchParams].length !== [...expected.searchParams].length) return null;
    if (![...expected.searchParams].every(([key, value]) =>
      actual.searchParams.getAll(key).length === 1 && actual.searchParams.get(key) === value)) return null;
    return actual.href;
  } catch { return null; }
}
